import '../styles/globals.scss'
import "@fortawesome/fontawesome-svg-core/styles.css"; // import Font Awesome CSS
import { config } from "@fortawesome/fontawesome-svg-core";
config.autoAddCss = false;
import type { AppProps } from 'next/app'
import { Kanit, Noto_Sans_Thai } from 'next/font/google'

// Two faces (DESIGN.md, "On Air"). Both cover Thai + Latin and are self-hosted
// by next/font under /_next/static/media.
//
// Noto Sans Thai is the reading face: everything people read. It is a variable
// font, so 400–700 ship as one file per subset instead of the four static
// weights per subset the previous face needed. It is preloaded, and next/font
// emits a metric-matched 'Noto Sans Thai Fallback' for the swap window. Next 16
// emits the literal family name, so the stacks in tailwind.config.js and
// globals.scss match it.
//
// Kanit is the broadcast face: plates, ranks, scores and segment titles. Only
// the two cuts the package uses are loaded (600 roman for tags and nav, 800
// italic for plates), and neither is preloaded: no Kanit text is the LCP
// element (the hero image is), so these files must not compete with it. While
// they load, the `font-display` stack falls back to the reading face.
//
// The webpack rule for next/font modules is `sideEffects: false`, so each
// returned object has to be referenced or its @font-face block is tree-shaken
// away — hence the `.variable` classes on <main> below. `.variable` (not
// `.className`) declares only a custom property, so Tailwind's font stacks stay
// the computed values.
const notoSansThai = Noto_Sans_Thai({
  subsets: ['thai', 'latin'],
  weight: 'variable',
  display: 'swap',
  fallback: ['sans-serif'],
  variable: '--font-reading',
})
const kanitRoman = Kanit({
  subsets: ['thai', 'latin'],
  weight: '600',
  style: 'normal',
  display: 'swap',
  preload: false,
  adjustFontFallback: false,
  variable: '--font-broadcast-roman',
})
const kanitItalic = Kanit({
  subsets: ['thai', 'latin'],
  weight: '800',
  style: 'italic',
  display: 'swap',
  preload: false,
  adjustFontFallback: false,
  variable: '--font-broadcast-italic',
})
import { Fragment, useEffect } from 'react';
import Head from 'next/head';
import { useRouter } from "next/router";
import * as gtag from "../utils/gtag";
import { SessionProvider } from "next-auth/react"
import { ThemeProvider } from "../contexts/ThemeContext"
import { SavedArticlesProvider } from "../contexts/SavedArticlesContext"
import { NotificationsProvider } from "../contexts/NotificationsContext"
import { ConsentProvider } from "../contexts/ConsentContext"
import BackToTop from "../components/ui/BackToTop"
import CookieConsent from "../components/ui/CookieConsent"
import ConsentGatedScripts from "../components/ConsentGatedScripts"
import { SITE_NAME, SITE_SHARE_IMAGE, TWITTER_HANDLE } from "../utils/seo"

const APP_TITLE = `${SITE_NAME} | ข่าวเกม รีวิวเกม ไกด์เกม บทความเกม`
const APP_DESCRIPTION =
  'เว็บไซต์เกมภาษาไทย ข่าวเกมล่าสุด รีวิวเกมพร้อมคะแนน ไกด์เกม และปฏิทินเกมออกใหม่ ครบทั้ง PC มือถือ และคอนโซล'

function MyApp({ Component, pageProps: {session, ...pageProps} }) {
  const router = useRouter();

  useEffect(() => {
    const handleRouteChange = (url: URL) => {
      gtag.pageview(url);
    };
    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events]);

  // Google Analytics, deferred. The 161 KB gtag.js and its ~100-250 ms of
  // execution on a mid-range phone were worth 3-4 Lighthouse points on mobile,
  // where it ran inside the measured window even with strategy="lazyOnload".
  // The queue is set up immediately — `window.gtag` is the standard dataLayer
  // stub, with Consent Mode defaults and the page config already pushed — so
  // nothing is lost: pageview(), event() and ConsentContext's consent update all
  // land in the queue and are sent when the library arrives. The library itself
  // loads on the first interaction (scroll, tap, key, pointer move) or after four
  // seconds, whichever is first; a visit that ends before either is the one
  // thing not recorded.
  useEffect(() => {
    const w = window as unknown as { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };
    w.dataLayer = w.dataLayer || [];
    if (typeof w.gtag !== "function") {
      w.gtag = function gtagStub() {
        (w.dataLayer as unknown[]).push(arguments);
      };
      w.gtag("js", new Date());
      w.gtag("consent", "default", {
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
        analytics_storage: "granted",
      });
      w.gtag("config", gtag.GA_TRACKING_ID, {
        page_path: window.location.pathname,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
      });
    }

    const events = ["pointerdown", "pointermove", "keydown", "touchstart", "scroll"] as const;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const cleanup = () => {
      events.forEach((e) => window.removeEventListener(e, load));
      if (timer) clearTimeout(timer);
    };
    const load = () => {
      cleanup();
      if (document.getElementById("gtag-lib")) return;
      const s = document.createElement("script");
      s.id = "gtag-lib";
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${gtag.GA_TRACKING_ID}`;
      document.head.appendChild(s);
    };
    events.forEach((e) => window.addEventListener(e, load, { once: true, passive: true }));
    timer = setTimeout(load, 4000);
    return cleanup;
  }, []);

  useEffect(() => {
    // Check if the site is under maintenance
    const isMaintenanceMode = process.env.NEXT_PUBLIC_MAINTENANCE_MODE === 'true';
    if (isMaintenanceMode && router.pathname !== '/maintenance') {
      router.push('/maintenance');
    } else if (!isMaintenanceMode && router.pathname === '/maintenance') {
      router.replace('/');
    }
  }, [router]);

    // Prevent rendering other components if in maintenance mode
    const isMaintenanceMode = process.env.NEXT_PUBLIC_MAINTENANCE_MODE === 'true';
    if (isMaintenanceMode && router.pathname !== '/maintenance') {
      return null; // Don't render anything while redirecting
    }
  return <Fragment>
    {/* Site-wide fallbacks only. Every key here is also emitted by PageSeo, so a
        page that renders one wins the next/head dedup and these never double up. */}
    <Head>
        <title>{APP_TITLE}</title>
        <meta
        name='viewport'
        content='width=device-width, initial-scale=1'/>
        <meta name='description' content={APP_DESCRIPTION} key="description"/>
        <meta property="og:type" content="website" key="og:type" />
        <meta property="og:site_name" content={SITE_NAME} key="og:site_name" />
        <meta property="og:title" content={APP_TITLE} key="title" />
        <meta property="og:description" content={APP_DESCRIPTION} key="og:description" />
        <meta property="og:locale" content="th_TH" key="og:locale" />
        {/* Absolute URL: relative og:image paths are silently dropped by
            Facebook, LINE and X, so every page missing its own image lost its
            share preview entirely. */}
        <meta property="og:image" content={SITE_SHARE_IMAGE} key="image" />
        <meta property="og:image:alt" content={SITE_NAME} key="og:image:alt" />
        <meta name="twitter:card" content="summary_large_image" key="twitter:card" />
        <meta name="twitter:site" content={TWITTER_HANDLE} key="twitter:site" />
        <meta name="twitter:title" content={APP_TITLE} key="twitter:title" />
        <meta name="twitter:description" content={APP_DESCRIPTION} key="twitter:description" />
        <meta name="twitter:image" content={SITE_SHARE_IMAGE} key="twitter:image" />
        <link rel="icon" href='/favicon.ico' />
    </Head>
    <ThemeProvider>
    <ConsentProvider>
    <SessionProvider session={session} refetchOnWindowFocus={false}>
    <SavedArticlesProvider>
    <NotificationsProvider>
    <main className={`font-sans ${notoSansThai.variable} ${kanitRoman.variable} ${kanitItalic.variable}`}>
    <Component {...pageProps} />
    <BackToTop />
    <CookieConsent />
    </main>
    {/* AdSense + Microsoft Clarity — injected only after cookie consent */}
    <ConsentGatedScripts />
    </NotificationsProvider>
    </SavedArticlesProvider>
    </SessionProvider>
    </ConsentProvider>
    </ThemeProvider>
    {/* Google Analytics (first-party only under Consent Mode, always on) is
        loaded by the deferred effect above, not by a <Script>. */}
  </Fragment>
}

export default MyApp
